'use client';
import { forwardRef, type ReactNode, type Ref } from 'react';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';

import { cn } from '../../utils/cn';
import { LoadingIcon } from '../icon/custom/loading';
import { ButtonProps, ButtonSize, ButtonVariant } from './types';

import styles from './button.module.css';

const sizeStyles: Record<ButtonSize, string> = {
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
};

const variantStyles: Record<ButtonVariant, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      destructive = false,
      inverse = false,
      className,
      trailingIcon,
      leadingIcon,
      ...props
    },
    ref,
  ) => {
    const isLinkMode = props.href !== undefined;
    // Link mode has no loading state.
    const loading = isLinkMode ? undefined : props.loading;

    const rootClassName = cn(
      styles.button,
      sizeStyles[size],
      variantStyles[variant],
      destructive && styles.destructive,
      inverse && styles.inverse,
      loading && styles.loading,
      className,
    );

    const renderBody =
      <RenderProps,>(children: ReactNode | ((values: RenderProps) => ReactNode)) =>
      (renderProps: RenderProps) => {
        const resolved = typeof children === 'function' ? children(renderProps) : children;

        return (
          <>
            {leadingIcon && <span className={styles.left}>{leadingIcon}</span>}

            <span className={styles.content}>{resolved}</span>

            {trailingIcon && <span className={styles.right}>{trailingIcon}</span>}

            {loading && (
              <div className={styles.loadingContainer}>
                <span className={styles.loadingIcon}>
                  <LoadingIcon />
                </span>
              </div>
            )}
          </>
        );
      };

    if (isLinkMode) {
      const { loading: _loading, isDisabled: _isDisabled, children, ...linkProps } = props;

      return (
        <AriaLink ref={ref as Ref<HTMLAnchorElement>} {...linkProps} className={cn(rootClassName, styles.link)}>
          {renderBody(children)}
        </AriaLink>
      );
    }

    const { href: _href, loading: _loading, children, ...buttonProps } = props;

    return (
      <AriaButton ref={ref as Ref<HTMLButtonElement>} {...buttonProps} isPending={!!loading} className={rootClassName}>
        {renderBody(children)}
      </AriaButton>
    );
  },
);

Button.displayName = 'Button';
